import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { admin, type TaskAdminSummary, type TaskUserSummary, type WorkflowSummary, type WorkflowStepRow } from '@/api/endpoints';
import type { Task, TaskStatus } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

const STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: 'Do zrobienia',
  IN_PROGRESS: 'W realizacji',
  AI_VERIFICATION: 'Weryfikacja AI',
  HUMAN_VERIFICATION: 'Weryfikacja człowieka',
  COMPLETED: 'Zakończone',
};

const STATUS_PILL: Record<TaskStatus, string> = {
  TODO: 'pill-water',
  IN_PROGRESS: 'pill-amber',
  AI_VERIFICATION: 'pill-ai',
  HUMAN_VERIFICATION: 'pill-earth',
  COMPLETED: 'pill-green',
};

function formatHours(h: number | null | undefined): string {
  if (h == null) return '—';
  if (h < 1) return '<1h';
  if (h < 48) return `${h} h`;
  const d = Math.round(h / 24);
  return `${d} d`;
}

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return many;
  if (last >= 2 && last <= 4) return few;
  return many;
}

export function TasksAdmin() {
  const [summary, setSummary] = useState<TaskAdminSummary | null>(null);
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [filterUserId, setFilterUserId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<TaskStatus | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [unassigned, setUnassigned] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null);
  const [workflowFilterUserId, setWorkflowFilterUserId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setErr(null);
    Promise.all([
      admin.tasks.summary(),
      admin.tasks.workflowSummary(),
    ]).then(([s, w]) => { setSummary(s); setWorkflow(w); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTasks(null);
    admin.tasks.list({
      userId: filterUserId ?? undefined,
      status: filterStatus || undefined,
      overdueOnly,
      unassigned,
    }).then(setTasks).catch(e => setErr(e?.message ?? String(e)));
  }, [filterUserId, filterStatus, overdueOnly, unassigned]);

  const selectedUser = useMemo(
    () => summary?.byUser.find(u => u.userId === filterUserId) ?? null,
    [summary, filterUserId]
  );

  if (loading) return <div className="text-ink-400">Ładowanie raportu…</div>;
  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!summary) return null;

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Raport zadań pracowników</h1>
          <div className="text-ink-400 text-[13px]">
            Przegląd wszystkich zadań w systemie z perspektywy admina — kto co ma w robocie, jak długo i ile przeterminowanych.
          </div>
        </div>
      </header>

      {/* Globalne KPI */}
      <div className="grid grid-cols-6 gap-3 mb-5">
        <KpiCard label="Wszystkie" value={summary.counts.total} subtitle={`${summary.counts.completed} zakończonych`} tone="ink"/>
        <KpiCard label="Otwarte" value={summary.counts.open} tone="water"/>
        <KpiCard label="W realizacji" value={summary.counts.inProgress} tone="amber"/>
        <KpiCard label="Do zrobienia" value={summary.counts.todo} tone="ink"/>
        <KpiCard label="Przeterminowane" value={summary.overdueCount} tone="red"/>
        <KpiCard label="Bez przypisania" value={summary.unassignedOpen}
          tone={summary.unassignedOpen > 0 ? 'red' : 'ink'}
          actionLabel={summary.unassignedOpen > 0 ? 'Pokaż' : undefined}
          onAction={() => { setUnassigned(true); setFilterUserId(null); setFilterStatus(''); setOverdueOnly(false); }}/>
      </div>

      {/* Per-user breakdown */}
      <div className="card mb-5">
        <div className="card-head">
          <div className="card-title">Per-pracownik</div>
          <span className="ml-auto text-[12px] text-ink-400">{summary.activeUsers} aktywnych w robocie</span>
        </div>
        <div className="card-body !p-0">
          <table className="table-hw">
            <thead>
              <tr>
                <th>Pracownik</th>
                <th className="text-center">Otwarte</th>
                <th className="text-center">TODO</th>
                <th className="text-center">W realizacji</th>
                <th className="text-center">Weryfikacja AI</th>
                <th className="text-center">Weryf. człowieka</th>
                <th className="text-center">Zakończone</th>
                <th className="text-center text-red-500">Przeterm.</th>
                <th>Średni wiek otwartych</th>
                <th>Najstarsze otwarte</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.byUser.length === 0 && (
                <tr><td colSpan={11} className="p-4 text-center text-ink-400">Brak danych — żaden pracownik nie ma zadań.</td></tr>
              )}
              {summary.byUser.map(u => (
                <UserRow key={u.userId} u={u}
                  isSelected={filterUserId === u.userId}
                  onSelect={() => {
                    setFilterUserId(filterUserId === u.userId ? null : u.userId);
                    setUnassigned(false); setFilterStatus(''); setOverdueOnly(false);
                  }}/>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filtry + lista zadań */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">
            {selectedUser ? `Zadania — ${selectedUser.fullName}`
              : unassigned ? 'Zadania bez przypisania'
              : 'Wszystkie zadania'}
          </div>
          <div className="ml-auto flex gap-2 items-center">
            <SearchableSelect value={filterStatus} onChange={v => setFilterStatus(v as TaskStatus | '')} className="select-sm">
              <option value="">Wszystkie statusy</option>
              {(Object.keys(STATUS_LABELS) as TaskStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </SearchableSelect>
            <label className="flex items-center gap-1.5 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)}/>
              Tylko przeterminowane
            </label>
            {(filterUserId !== null || filterStatus || overdueOnly || unassigned) && (
              <button onClick={() => { setFilterUserId(null); setFilterStatus(''); setOverdueOnly(false); setUnassigned(false); }}
                className="btn btn-secondary btn-sm">
                <Icon name="x" className="w-3.5 h-3.5"/> Wyczyść
              </button>
            )}
          </div>
        </div>
        <div className="card-body !p-0">
          {tasks == null && <div className="p-4 text-ink-400">Ładowanie zadań…</div>}
          {tasks && tasks.length === 0 && <div className="p-4 text-ink-400">Brak zadań pasujących do filtrów.</div>}
          {tasks && tasks.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Tytuł</th>
                  <th>Opracowanie</th>
                  <th>Krok</th>
                  <th>Status</th>
                  <th>Przypisane</th>
                  <th>Deadline</th>
                  <th>W bieżącym statusie</th>
                  <th>Priorytet</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(z => <TaskRow key={z.id} z={z}/>)}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ====== Workflow opracowań ====== */}
      {workflow && (
        <>
          <div className="mt-8 mb-3">
            <h2 className="text-[18px] font-bold text-ink-900">Workflow opracowań — kto trzyma jakie kroki</h2>
            <div className="text-ink-400 text-[13px]">Aktywne kroki (TODO + W toku) ze wszystkich opracowań — niezależnie od kanban-zadań.</div>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-5">
            <KpiCard label="Aktywne kroki" value={workflow.totalActiveSteps} tone="ink"
              subtitle={`${workflow.totalInProgress} w toku · ${workflow.totalTodo} czeka`}/>
            <KpiCard label="W toku" value={workflow.totalInProgress} tone="amber"/>
            <KpiCard label="Czekają (TODO)" value={workflow.totalTodo} tone="water"/>
            <KpiCard label="Bez przypisania" value={workflow.unassignedActive}
              tone={workflow.unassignedActive > 0 ? 'red' : 'ink'}
              actionLabel={workflow.unassignedActive > 0 ? 'Pokaż' : undefined}
              onAction={() => setWorkflowFilterUserId(-1)}/>
          </div>

          <div className="card mb-5">
            <div className="card-head">
              <div className="card-title">Per-pracownik (workflow)</div>
              <span className="ml-auto text-[12px] text-ink-400">{workflow.byUser.length} osób</span>
            </div>
            <div className="card-body !p-0">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-ink-100 text-left text-[11px] text-ink-500 uppercase">
                    <th className="px-3 py-2">Pracownik</th>
                    <th className="px-3 py-2 text-center">Aktywne kroki</th>
                    <th className="px-3 py-2 text-center">W toku</th>
                    <th className="px-3 py-2 text-center">Czeka (TODO)</th>
                    <th className="px-3 py-2 text-center">Opracowania</th>
                    <th className="px-3 py-2">Najstarszy aktywny</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {workflow.byUser.length === 0 && (
                    <tr><td colSpan={7} className="p-4 text-center text-ink-400">Brak aktywnych przypisań do kroków.</td></tr>
                  )}
                  {workflow.byUser.map(u => {
                    const isSelected = workflowFilterUserId === u.userId;
                    return (
                      <tr key={u.userId}
                          className={'hover:bg-ink-50 cursor-pointer ' + (isSelected ? 'bg-water-50' : '')}
                          onClick={() => setWorkflowFilterUserId(isSelected ? null : u.userId)}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">{u.initials}</span>
                            <span className="font-semibold text-ink-900">{u.fullName}</span>
                          </div>
                        </td>
                        <td className="text-center font-bold text-ink-900">{u.totalActive}</td>
                        <td className="text-center text-amber-600">{u.inProgress}</td>
                        <td className="text-center text-water-600">{u.todo}</td>
                        <td className="text-center text-ink-700">{u.distinctStudies}</td>
                        <td>
                          {u.oldestActiveHours != null ? (
                            <div className="text-[12px]">
                              <span className="font-mono text-ink-700">{formatHours(u.oldestActiveHours)}</span>
                              <span className="text-ink-500 ml-1.5">— {u.oldestStudyNumber} · {u.oldestStepCode}</span>
                            </div>
                          ) : <span className="text-ink-400">—</span>}
                        </td>
                        <td><Icon name={isSelected ? 'chevron-down' : 'chevron'} className="text-ink-400 w-3 h-3"/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">
                {workflowFilterUserId === -1 ? 'Kroki bez przypisania'
                  : workflowFilterUserId
                  ? `Kroki — ${workflow.byUser.find(u => u.userId === workflowFilterUserId)?.fullName ?? '?'}`
                  : 'Wszystkie aktywne kroki workflow'}
              </div>
              {workflowFilterUserId !== null && (
                <button onClick={() => setWorkflowFilterUserId(null)} className="ml-auto btn btn-secondary btn-sm">
                  <Icon name="x" className="w-3.5 h-3.5"/> Wyczyść
                </button>
              )}
            </div>
            <div className="card-body !p-0">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-ink-100 text-left text-[11px] text-ink-500 uppercase">
                    <th className="px-3 py-2">Krok</th>
                    <th className="px-3 py-2">Opracowanie</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Przypisane</th>
                    <th className="px-3 py-2">Wiek</th>
                    <th className="px-3 py-2">Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = workflow.rows.filter(r => {
                      if (workflowFilterUserId === null) return true;
                      if (workflowFilterUserId === -1) return r.assigneeId == null;
                      return r.assigneeId === workflowFilterUserId;
                    });
                    if (filtered.length === 0) {
                      return <tr><td colSpan={6} className="p-4 text-center text-ink-400">Brak kroków pasujących do filtra.</td></tr>;
                    }
                    return filtered.map(r => <WorkflowRow key={r.stepId} r={r}/>);
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function WorkflowRow({ r }: { r: WorkflowStepRow }) {
  const isInProgress = r.status === 'IN_PROGRESS';
  const today = new Date(); today.setHours(0,0,0,0);
  const dl = r.studyDeadline ? new Date(r.studyDeadline) : null;
  const isOverdue = dl != null && dl < today;
  return (
    <tr className={'hover:bg-ink-50 ' + (isOverdue ? 'bg-red-50/40' : '')}>
      <td className="px-3 py-2">
        <span className="font-mono text-[11px] text-ink-700 mr-1.5">{r.stepCode}</span>
        <span className="text-ink-800">{r.stepName}</span>
      </td>
      <td className="px-3 py-2">
        {r.studyId
          ? <NavLink to={`/studies/${r.studyId}`} className="text-water-600 no-underline hover:underline font-mono text-[11px]">{r.studyNumber}</NavLink>
          : '—'}
      </td>
      <td className="px-3 py-2">
        <span className={'pill ' + (isInProgress ? 'pill-amber' : 'pill-water')}>
          {isInProgress ? 'w toku' : 'czeka'}
        </span>
      </td>
      <td className="px-3 py-2">
        {r.assigneeFullName ? (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink-100 text-[9px] font-bold text-ink-700">{r.assigneeInitials}</span>
            <span className="text-ink-700">{r.assigneeFullName}</span>
          </div>
        ) : <span className="pill pill-red">nieprzypisane</span>}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-ink-700">{formatHours(r.ageHours)}</td>
      <td className={'px-3 py-2 font-mono text-[11px] ' + (isOverdue ? 'text-red-500 font-bold' : 'text-ink-600')}>
        {r.studyDeadline ?? '—'}{isOverdue && ' ⚠'}
      </td>
    </tr>
  );
}

function UserRow({ u, isSelected, onSelect }: { u: TaskUserSummary; isSelected: boolean; onSelect: () => void }) {
  const c = u.counts;
  return (
    <tr className={'hover:bg-ink-50 cursor-pointer ' + (isSelected ? 'bg-water-50' : '')} onClick={onSelect}>
      <td>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">{u.initials}</span>
          <div className="min-w-0">
            <div className="font-semibold text-ink-900 truncate">{u.fullName}{!u.active && <span className="ml-2 pill pill-amber">nieaktywny</span>}</div>
            <div className="text-[10px] text-ink-400 font-mono">{u.roles.join(' · ')}</div>
          </div>
        </div>
      </td>
      <td className="text-center font-bold text-ink-900">{c.open}</td>
      <td className="text-center text-ink-700">{c.todo}</td>
      <td className="text-center text-amber-600">{c.inProgress}</td>
      <td className="text-center text-ai-600">{c.aiVerification}</td>
      <td className="text-center text-earth-600">{c.humanVerification}</td>
      <td className="text-center text-ink-500">{c.completed}</td>
      <td className="text-center">
        {u.overdueCount > 0
          ? <span className="pill pill-red font-bold">{u.overdueCount}</span>
          : <span className="text-ink-400">0</span>}
      </td>
      <td className="font-mono text-[12px] text-ink-700">{formatHours(u.avgOpenAgeHours)}</td>
      <td>
        {u.oldestOpenAgeHours != null ? (
          <div className="text-[12px]">
            <span className="font-mono text-ink-700">{formatHours(u.oldestOpenAgeHours)}</span>
            {u.oldestOpenTaskTitle && <span className="text-ink-500 ml-1.5 truncate inline-block max-w-[200px] align-middle">— {u.oldestOpenTaskTitle}</span>}
          </div>
        ) : <span className="text-ink-400">—</span>}
      </td>
      <td><Icon name={isSelected ? 'chevron-down' : 'chevron'} className="text-ink-400 w-3 h-3"/></td>
    </tr>
  );
}

function TaskRow({ z }: { z: Task }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const dl = z.deadline ? new Date(z.deadline) : null;
  const isOverdue = z.status !== 'COMPLETED' && dl && dl < today;
  const sinceMs = z.statusChangedAt ? Date.now() - new Date(z.statusChangedAt).getTime() : null;
  const sinceHours = sinceMs != null ? Math.floor(sinceMs / (1000 * 60 * 60)) : null;
  const sinceDays = sinceHours != null ? Math.floor(sinceHours / 24) : null;
  const sinceLabel = sinceHours == null ? '—'
    : sinceHours < 24 ? `${sinceHours} ${plural(sinceHours, 'godzina', 'godziny', 'godzin')}`
    : `${sinceDays} ${plural(sinceDays!, 'dzień', 'dni', 'dni')}`;

  return (
    <tr className={'hover:bg-ink-50 ' + (isOverdue ? 'bg-red-50/40' : '')}>
      <td className="max-w-[300px]">
        <div className="font-semibold text-ink-800 truncate max-w-[300px]" title={z.title}>{z.title}</div>
        {z.description && <div className="text-[11px] text-ink-400 truncate max-w-[300px]" title={z.description}>{z.description}</div>}
      </td>
      <td className="text-[12px]">
        {z.studyId
          ? <NavLink to={`/studies/${z.studyId}`} className="text-water-600 no-underline hover:underline font-mono text-[11px]">{z.studyNumber}</NavLink>
          : <span className="text-ink-400">—</span>}
      </td>
      <td>{z.stepCode ? <span className="pill pill-earth font-mono text-[10px]">{z.stepCode}</span> : <span className="text-ink-400">—</span>}</td>
      <td><span className={'pill ' + STATUS_PILL[z.status]}>{z.statusName}</span></td>
      <td className="text-[12px]">
        {z.assigneeFullName ? (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-ink-100 text-[9px] font-bold text-ink-700">{z.assigneeInitials}</span>
            <span className="text-ink-700">{z.assigneeFullName}</span>
          </div>
        ) : <span className="pill pill-red">nieprzypisane</span>}
      </td>
      <td className={'font-mono text-[12px] ' + (isOverdue ? 'text-red-500 font-bold' : 'text-ink-600')}>
        {z.deadline ?? '—'}{isOverdue && ' ⚠'}
      </td>
      <td className="font-mono text-[12px] text-ink-700">{sinceLabel}</td>
      <td><span className="pill pill-water">{z.priorityName}</span></td>
    </tr>
  );
}

function KpiCard({ label, value, subtitle, tone, actionLabel, onAction }: {
  label: string; value: number | string; subtitle?: string;
  tone?: 'ink' | 'water' | 'amber' | 'red';
  actionLabel?: string; onAction?: () => void;
}) {
  const toneClass = tone === 'red' ? 'text-red-500'
    : tone === 'amber' ? 'text-amber-600'
    : tone === 'water' ? 'text-water-600'
    : 'text-ink-800';
  return (
    <div className="card">
      <div className="card-body py-3">
        <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">{label}</div>
        <div className={'text-[26px] font-bold leading-none ' + toneClass}>{value}</div>
        {subtitle && <div className="text-[11px] text-ink-400 mt-1">{subtitle}</div>}
        {actionLabel && (
          <button onClick={onAction} className="mt-2 text-[11px] text-water-600 hover:underline">{actionLabel} →</button>
        )}
      </div>
    </div>
  );
}
